import { Sides } from "@project/emerge-types";
import type { Vector3 } from "three";
import * as THREE from "three";
import { LTCaseUserOption } from "../../enum/LTCaseUserOption";
import { PaddingStyle } from "../../enum/PaddingStyle";
import { PadSide } from "../../enum/PadSide";
import { LTPartType } from "../../enum/PartType";
import type { PaddingType } from "../../interfaces/Padding";
import type { PadDefinition } from "../../interfaces/PadProperties";
import { PadHelper } from "../../structure/PadHelper";
import { DefaultGroupName } from "../../structure/Rect";
import { RectPropertiesBySide } from "../../structure/RectProperties";
import { OptionDBInstance } from "../../user_options/options_db";
import { getVector3Index } from "./InteriorHelpers";

/**
 * Builds a synthetic `PaddingType` carrying the requested custom strength.
 *
 * Intentional trade-offs (matches the legacy `Pad` behavior in `oldcode/frontend.txt`):
 * - `name` is empty. Downstream price code (`getPriceForPart` -> case "Pad") uses
 *   `props.partSelection?.name` as a flag to decide between area-based pricing
 *   (m², known catalog foam) and volumetric pricing (m³, generic `STN_PLN_*`
 *   code per style). Empty name routes us to the volumetric branch, which is
 *   what the old code did when `OptionDB.get(...)` returned `undefined`.
 * - `strength` carries the exact requested value so renderers, BOM, and the
 *   volumetric price (`w * h * strength * 1e-9 m³`) all see the real thickness
 *   instead of a rounded catalog value.
 */
function createSyntheticPaddingType(
  style: PaddingStyle,
  padStrength: number,
): PaddingType {
  return {
    name: "",
    description: "Schaumstoff",
    kind: LTCaseUserOption.PaddingType,
    deleted: false,
    strength: padStrength,
    style,
  };
}

export function getPaddingPartSelection(
  style: PaddingStyle,
  padStrength: number,
): PaddingType {
  try {
    return OptionDBInstance.getOrThrow(LTCaseUserOption.PaddingType, {
      style,
      strength: padStrength,
    } as PaddingType);
  } catch {
    // No silent rounding to the closest catalog strength: that would quietly
    // change both the rendered thickness and the BOM/price code (e.g. SST1042
    // when the user configured 280 mm custom foam). Instead we synthesize a
    // PaddingType with the exact strength. The Pad still renders at the
    // requested thickness and pricing falls through to the volumetric branch
    // (`STN_PLN_29` / `STN_PEN_5023` / `STN_PEW_5023`, unit m³), mirroring the
    // old `Pad` fallback when `OptionDB.get(...)` returned `undefined`.
    return createSyntheticPaddingType(style, padStrength);
  }
}

export function buildBottomPad(
  center: Vector3,
  caseInteriorSize: Vector3,
  sizeWithPadding: Vector3,
  padding: { [side in Sides]: number },
  style: PaddingStyle,
  partSelection: PaddingType,
  buildstepName: string,
): PadDefinition | null {
  const bottomPaddingHeight = padding[Sides.BOTTOM];
  // Skip pads with 0mm strength
  if (bottomPaddingHeight <= 0) {
    return null;
  }

  const padPosition = center.clone();
  padPosition.y = -caseInteriorSize.y / 2;
  const pad: PadDefinition = {
    id: "bottom_pad",
    position: padPosition,
    partSelection,
    side: Sides.BOTTOM,
    size: [
      sizeWithPadding.x - padding[Sides.LEFT] - padding[Sides.RIGHT],
      sizeWithPadding.z - padding[Sides.FRONT] - padding[Sides.BACK],
    ],
    strength: bottomPaddingHeight,
    style,
    type: LTPartType.Pad,
    rotation: new THREE.Euler(0, 0, 0),
    buildstepName,
    group: DefaultGroupName,
    orientation: 0,
    cutouts: [],
  };
  pad.position.z -= (padding[Sides.FRONT] - padding[Sides.BACK]) / 2;
  pad.position.x -= (padding[Sides.RIGHT] - padding[Sides.LEFT]) / 2;
  return pad;
}

export function adjustPadHelperForHoodAndSides(
  padHelper: PadHelper,
  side: Sides,
  isHood: boolean,
): void {
  for (const padSide of [PadSide.Left, PadSide.Right]) {
    if (isHood) {
      if (side === Sides.LEFT || side === Sides.RIGHT) {
        if (
          !padHelper.isAtCaseOuter ||
          padHelper.sideTouchesCaseOuter(padSide)
        ) {
          padHelper.shrink(padSide);
        }
      } else if (
        !padHelper.isAtCaseOuter &&
        padHelper.sideTouchesCaseOuter(padSide)
      ) {
        padHelper.shrink(padSide);
      }
    } else if (side === Sides.LEFT || side === Sides.RIGHT) {
      padHelper.shrink(padSide);
    }
  }
}

interface CreatePaddedCubeOptions {
  buildstepName: string;
  innerPaddingHeight?: number;
  style?: PaddingStyle;
}

export function createPaddedCube(
  caseInteriorSize: Vector3,
  padding: { [side in Sides]: number },
  center: Vector3,
  sizeWithPadding: Vector3,
  sidePaddingHeight: number,
  options: CreatePaddedCubeOptions,
): PadDefinition[] {
  const {
    buildstepName,
    innerPaddingHeight = sidePaddingHeight,
    style = PaddingStyle.STANDARD,
  } = options;
  const result: PadDefinition[] = [];
  const isHood = sidePaddingHeight !== innerPaddingHeight;

  for (const side in padding) {
    if (!Object.hasOwn(padding, side)) continue;
    const numericSide = Number(side) as Sides;
    const padStrength = padding[numericSide];
    if (padStrength <= 0) continue;

    const rectProps = RectPropertiesBySide[numericSide];
    if (!rectProps) continue;
    const { normal, normalDir, horizontal } = rectProps;

    // align center.y for current calculation
    center.y = (caseInteriorSize.y - innerPaddingHeight) / 2;

    const partSelection = getPaddingPartSelection(style, padStrength);

    if (numericSide === Sides.TOP) {
      // Top padding is done in the cover
      continue;
    }

    if (numericSide === Sides.BOTTOM) {
      const bottomPad = buildBottomPad(
        center,
        caseInteriorSize,
        sizeWithPadding,
        padding,
        style,
        partSelection,
        buildstepName,
      );
      if (bottomPad) {
        result.push(bottomPad);
      }
      continue;
    }

    const padPosition = center
      .clone()
      .add(
        normalDir
          .clone()
          .multiplyScalar(
            sizeWithPadding.getComponent(getVector3Index(normal)) / 2,
          ),
      );
    padPosition.y = (-caseInteriorSize.y + sidePaddingHeight) / 2;

    const padHelper = new PadHelper(
      numericSide,
      sizeWithPadding.getComponent(getVector3Index(horizontal)),
      padPosition,
      caseInteriorSize,
      padding,
    );
    adjustPadHelperForHoodAndSides(padHelper, numericSide, isHood);

    result.push({
      ...padHelper.getProperties(
        padHelper.isAtCaseOuter ? sidePaddingHeight : innerPaddingHeight,
      ),
      id: "", // ID wird im BuildStep überschrieben (z.B. volume_pad_${counter})
      partSelection,
      style,
      side: numericSide,
      type: LTPartType.Pad,
      rotation: new THREE.Euler(0, 0, 0),
      buildstepName,
      group: DefaultGroupName,
      orientation: 0,
      cutouts: [],
    });
  }
  return result;
}
